import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import api from '../lib/api';
import { Plus, Trash2, Download, Lock, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart, Line } from 'recharts';
import { fmt } from '../lib/format';

const CURRENCIES = [
  { code: 'USD', symbol: '$' }, { code: 'PYG', symbol: '₲' }, { code: 'ARS', symbol: '$' },
  { code: 'MXN', symbol: '$' }, { code: 'EUR', symbol: '€' }, { code: 'BRL', symbol: 'R$' },
];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// ---- Category Progress Bar Component ----
const CategoryProgressBar = ({ label, budget, actual, color, theme, sym }) => {
  const pct = budget > 0 ? Math.min(100, Math.round((actual / budget) * 100)) : 0;
  const over = budget > 0 && actual > budget;
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <span className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>{label}</span>
        <span className="text-xs shrink-0 tabular-nums" style={{ color: over ? theme.error : theme.textMuted }}>
          {sym} {fmt(actual)} <span style={{ color: theme.textMuted }}>/ {sym} {fmt(budget)}</span>
        </span>
      </div>
      <div className="relative w-full h-2.5 rounded-full overflow-hidden" style={{ background: theme.elevated }}>
        <div className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{ width: `${Math.min(100, pct)}%`, background: over ? theme.error : color, boxShadow: over ? `0 0 12px ${theme.error}60` : 'none' }} />
      </div>
      <p className="text-[10px] mt-1" style={{ color: over ? theme.error : theme.textMuted }}>
        {over ? `Excedido ${sym} ${fmt(actual - budget)}` : `${pct}% del presupuesto`}
      </p>
    </div>
  );
};
// Returns black or white text depending on background luminance
const pickTextColor = (hex) => {
  if (!hex || typeof hex !== 'string') return '#fff';
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#0A0606' : '#fff';
};

const Row = ({ row, fields, onUpdate, onDelete, theme, sym, color, knownIncomeCats = [] }) => {
  const [local, setLocal] = useState(row);
  useEffect(() => { setLocal(row); }, [row.id]);
  const save = (key, val) => { const u = { ...local, [key]: val }; setLocal(u); onUpdate(u); };
  const [creatingNewIncomeCat, setCreatingNewIncomeCat] = useState(
    () => !row.label && (knownIncomeCats || []).length === 0
  );
  return (
    <tr style={{ borderBottom: `1px solid ${theme.border}` }} className="group">
      {fields.map((f, i) => {
        const val = local[f.key] ?? '';
        if (f.type === 'label') return (
          <td key={i} className="p-2 text-left">
            <Input value={val} onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })} onBlur={(e) => save(f.key, e.target.value)}
              placeholder={f.placeholder} className="text-sm h-8 border-0 px-2" style={{ background: 'transparent', color: theme.textPrimary, minWidth: 0 }} />
          </td>
        );
        if (f.type === 'cat-income') {
          // Income category — allow picking existing or creating new
          const options = Array.from(new Set([...(knownIncomeCats || []), ...(local.label ? [local.label] : [])]));
          if (creatingNewIncomeCat || options.length === 0) {
            return (
              <td key={i} className="p-2 text-left">
                <Input autoFocus={creatingNewIncomeCat} value={val}
                  onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                  onBlur={(e) => { setCreatingNewIncomeCat(false); save(f.key, e.target.value); }}
                  placeholder={f.placeholder} className="text-sm h-8 border-0 px-2" style={{ background: 'transparent', color: theme.textPrimary, minWidth: 0 }} />
              </td>
            );
          }
          return (
            <td key={i} className="p-2 text-left">
              <select value={val || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__new__') { setCreatingNewIncomeCat(true); return; }
                  setLocal({ ...local, [f.key]: v });
                  save(f.key, v);
                }}
                className="h-8 text-sm bg-transparent border-0 outline-none w-full px-2 cursor-pointer rounded hover:bg-white/5"
                style={{ color: theme.textPrimary, colorScheme: 'dark' }}>
                {!val && <option value="" disabled>{f.placeholder || 'Categoría'}</option>}
                {options.map(c => <option key={c} value={c} style={{ background: theme.card, color: theme.textPrimary }}>{c}</option>)}
                <option value="__new__" style={{ background: theme.card, color: theme.accent }}>+ Crear nueva</option>
              </select>
            </td>
          );
        }
        if (f.type === 'date') return (
          <td key={i} className="p-1 w-[110px]">
            <Input type="date" value={val} onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })} onBlur={(e) => save(f.key, e.target.value)}
              className="text-xs h-8 border-0 px-1.5" style={{ background: 'transparent', color: theme.textSecondary, colorScheme: 'dark' }} />
          </td>
        );
        if (f.type === 'check') {
          const checked = !!val;
          return (
            <td key={i} className="p-1 text-center w-[100px]">
              <button onClick={() => save(f.key, !checked)}
                className="w-6 h-6 rounded-md flex items-center justify-center mx-auto transition-all hover:scale-110"
                style={{
                  background: checked ? color : 'transparent',
                  border: `1.5px solid ${color}`,
                  boxShadow: checked ? `0 0 8px ${color}80` : 'none',
                }}>
                {checked && <span className="text-xs font-bold" style={{ color: pickTextColor(color) }}>✓</span>}
              </button>
            </td>
          );
        }
        return (
          <td key={i} className="p-1 w-[120px] text-right">
            <Input type="number" inputMode="decimal" value={val === 0 || val === '0' ? '' : val} onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })} onBlur={(e) => save(f.key, parseFloat(e.target.value) || 0)}
              placeholder="0" className="text-sm h-8 border-0 px-2 text-right tabular-nums" style={{ background: 'transparent', color: theme.textPrimary }} />
          </td>
        );
      })}
      <td className="p-1 w-7">
        <button onClick={() => onDelete(row.id)} className="opacity-0 group-hover:opacity-100 p-1" style={{ color: theme.textMuted }}><Trash2 size={12} /></button>
      </td>
    </tr>
  );
};

// ---- Section component ----
const Section = ({ title, color, rows, fields, onAdd, onUpdate, onDelete, theme, sym, showTotal = true, knownIncomeCats }) => {
  const totalKeys = ['budget', 'actual', 'expected', 'monthly'];
  const totals = {};
  totalKeys.forEach(k => { totals[k] = rows.reduce((s, r) => s + (parseFloat(r[k]) || 0), 0); });
  const headerText = pickTextColor(color);
  return (
    <div className="rounded-md overflow-hidden flex flex-col" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
      <div className="flex items-center justify-between px-2 py-1.5" style={{ background: color, color: headerText }}>
        <span className="text-[11px] font-bold uppercase tracking-widest truncate">{title}</span>
        <button data-testid={`add-${title.toLowerCase().replace(/\s/g, '-')}`} onClick={onAdd} className="w-5 h-5 rounded flex items-center justify-center hover:bg-black/15 shrink-0" style={{ color: headerText }}>
          <Plus size={12} />
        </button>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          {fields.map((f, i) => (
            <col key={i} style={{ width: f.type === 'label' ? 'auto' : f.type === 'date' ? '110px' : f.type === 'check' ? '100px' : '120px' }} />
          ))}
          <col style={{ width: '28px' }} />
        </colgroup>
        <thead>
          <tr style={{ background: theme.elevated }}>
            {fields.map((f, i) => (
              <th key={i} className={`p-2 text-[10px] uppercase tracking-wider whitespace-nowrap ${f.type === 'label' ? 'text-left pl-2.5' : f.type === 'date' || f.type === 'check' ? 'text-center' : 'text-right pr-2.5'}`} style={{ color: theme.textMuted }}>
                {f.label}
              </th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={fields.length + 1} className="text-center text-xs py-5" style={{ color: theme.textMuted }}>Sin datos</td></tr>}
          {rows.map(r => <Row key={r.id} row={r} fields={fields} onUpdate={onUpdate} onDelete={onDelete} theme={theme} sym={sym} color={color} knownIncomeCats={knownIncomeCats} />)}
        </tbody>
        {showTotal && rows.length > 0 && (
          <tfoot>
            <tr style={{ background: `${color}25`, borderTop: `1px solid ${color}` }}>
              <td className="p-2.5 text-[11px] font-bold uppercase" style={{ color }}>Total</td>
              {fields.slice(1).map((f, i) => (
                <td key={i} className={f.type === 'date' || f.type === 'check' ? 'p-1 text-center' : 'p-2.5 text-right text-sm font-bold whitespace-nowrap tabular-nums'} style={{ color }}>
                  {f.type === 'check' ? (rows.filter(r => r[f.key]).length > 0 ? `${rows.filter(r => r[f.key]).length}/${rows.length}` : '') : f.type !== 'date' && totals[f.key] !== undefined ? `${sym} ${fmt(totals[f.key])}` : ''}
                </td>
              ))}
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
    </div>
  );
};

// ---- TrackerTab: shows daily expense entries with day/week/month filter ----
const TrackerTab = ({ rows, theme, sym, fmt, dailyBudget, categories = [], onUpdate, onDelete, onAdd, onAddFull }) => {
  const [view, setView] = useState('day');
  const [dateRef, setDateRef] = useState(new Date().toISOString().split('T')[0]);
  // Quick-add modal state — lets the user log an expense for ANY date with category + amount
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddDate, setQuickAddDate] = useState(new Date().toISOString().split('T')[0]);
  const [quickAddCategory, setQuickAddCategory] = useState('');
  const [quickAddAmount, setQuickAddAmount] = useState('');
  const [quickAddSaving, setQuickAddSaving] = useState(false);

  const inRange = (rowDate) => {
    const d = new Date(rowDate); const ref = new Date(dateRef);
    if (view === 'day') return rowDate === dateRef;
    if (view === 'week') {
      const weekStart = new Date(ref); weekStart.setDate(ref.getDate() - ref.getDay());
      const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
      return d >= weekStart && d <= weekEnd;
    }
    if (view === 'month') return d.getMonth() === ref.getMonth() && d.getFullYear() === ref.getFullYear();
    return true;
  };
  const filtered = rows.filter(r => inRange(r.date)).sort((a, b) => (a.date > b.date ? 1 : -1));
  const totalSpent = filtered.reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);

  // Group by date for "Pasado/Quedó" calculation
  const byDate = {};
  filtered.forEach(r => { if (!byDate[r.date]) byDate[r.date] = []; byDate[r.date].push(r); });
  const sortedDates = Object.keys(byDate).sort((a, b) => (a < b ? 1 : -1));

  return (
    <div className="space-y-4">
      <div className="rounded-xl p-5" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-2xl font-light tracking-tight" style={{ color: theme.textPrimary }}>Tracker de Gastos del día</h2>
            <p className="text-[11px] mt-1" style={{ color: theme.textMuted }}>Detalle de cada gasto registrado · si quedaste o pasaste el presupuesto del día</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={dateRef} onChange={(e) => setDateRef(e.target.value)}
              className="text-xs h-9 px-3 rounded-md outline-none"
              style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}`, colorScheme: 'dark' }}
              data-testid="tracker-date-picker" />
            <div className="flex rounded-md overflow-hidden" style={{ border: `1px solid ${theme.border}` }}>
              {['day', 'week', 'month'].map(v => (
                <button key={v} onClick={() => setView(v)}
                  className="text-[11px] uppercase tracking-wider px-3 py-2 transition-colors"
                  style={{ background: view === v ? theme.primary : 'transparent', color: view === v ? '#fff' : theme.textMuted }}>
                  {v === 'day' ? 'Día' : v === 'week' ? 'Semana' : 'Mes'}
                </button>
              ))}
            </div>
            {onAdd && view === 'day' && (
              <button onClick={() => onAdd(dateRef)}
                className="text-xs h-9 px-3 rounded-md font-medium whitespace-nowrap"
                style={{ background: theme.accent, color: theme.bg }}
                data-testid="tracker-add-for-date">
                + Agregar para este día
              </button>
            )}
            {onAdd && (
              <button onClick={() => { setQuickAddDate(dateRef); setQuickAddOpen(true); }}
                className="text-xs h-9 px-3 rounded-md font-medium whitespace-nowrap"
                style={{ background: theme.primary, color: '#fff' }}
                data-testid="tracker-quick-add-any-date">
                + Gasto con cualquier fecha
              </button>
            )}
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="rounded-md p-3" style={{ background: theme.elevated }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Gastos</p>
            <p className="text-lg font-semibold tabular-nums mt-1" style={{ color: theme.error }}>{sym} {fmt(totalSpent)}</p>
          </div>
          <div className="rounded-md p-3" style={{ background: theme.elevated }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Movimientos</p>
            <p className="text-lg font-semibold tabular-nums mt-1" style={{ color: theme.textPrimary }}>{filtered.length}</p>
          </div>
          <div className="rounded-md p-3" style={{ background: theme.elevated }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Días</p>
            <p className="text-lg font-semibold tabular-nums mt-1" style={{ color: theme.textPrimary }}>{Object.keys(byDate).length}</p>
          </div>
          <div className="rounded-md p-3" style={{ background: theme.elevated }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Promedio/día</p>
            <p className="text-lg font-semibold tabular-nums mt-1" style={{ color: theme.accent }}>
              {sym} {fmt(Object.keys(byDate).length > 0 ? totalSpent / Object.keys(byDate).length : 0)}
            </p>
          </div>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm italic mb-1" style={{ color: theme.textMuted }}>No hay gastos registrados en este período</p>
            <p className="text-[11px] mb-3" style={{ color: theme.textMuted }}>
              Podés agregar gastos retroactivamente para cualquier día — útil si te olvidaste de registrarlos.
            </p>
            {onAdd && (
              <button onClick={() => onAdd(view === 'day' ? dateRef : new Date().toISOString().split('T')[0])}
                className="text-xs px-4 py-2 rounded-md font-medium" style={{ background: theme.primary, color: '#fff' }}
                data-testid="tracker-empty-add">
                + Agregar gasto {view === 'day' ? `a ${new Date(dateRef + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}` : 'a hoy'}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {sortedDates.map(date => {
              const daily = byDate[date];
              const total = daily.reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
              const status = dailyBudget > 0 ? (total > dailyBudget ? 'over' : 'under') : null;
              const dt = new Date(date + 'T00:00:00');
              return (
                <div key={date} className="rounded-lg overflow-hidden" style={{ border: `1px solid ${theme.border}` }}>
                  <div className="flex items-center justify-between px-4 py-2" style={{ background: theme.elevated }}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold capitalize" style={{ color: theme.textPrimary }}>
                        {dt.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </span>
                      <span className="text-[10px] tabular-nums" style={{ color: theme.textMuted }}>{daily.length} {daily.length === 1 ? 'gasto' : 'gastos'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold tabular-nums" style={{ color: theme.error }}>{sym} {fmt(total)}</span>
                      {status && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider"
                          style={{ background: status === 'over' ? `${theme.error}25` : `${theme.success}25`, color: status === 'over' ? theme.error : theme.success }}>
                          {status === 'over' ? `+${sym} ${fmt(total - dailyBudget)} excedido` : `${sym} ${fmt(dailyBudget - total)} restante`}
                        </span>
                      )}
                    </div>
                  </div>
                  <table className="w-full">
                    <tbody>
                      {daily.map(r => (
                        <TrackerRow key={r.id} row={r} categories={categories} theme={theme} sym={sym} fmt={fmt}
                          onUpdate={onUpdate} onDelete={onDelete} />
                      ))}
                      <tr style={{ borderTop: `1px dashed ${theme.border}` }}>
                        <td colSpan={4} className="p-2">
                          <button onClick={() => onAdd && onAdd(date)}
                            className="text-[11px] flex items-center gap-1 hover:underline"
                            style={{ color: theme.accent }}>
                            + Agregar gasto a {dt.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick-add modal — log an expense for ANY date with category + amount in one shot */}
      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent
          className="max-w-md"
          style={{ background: theme.card, border: `1px solid ${theme.border}` }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: theme.textPrimary }}>Nuevo gasto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: theme.textMuted }}>Fecha</label>
              <Input
                type="date"
                value={quickAddDate}
                onChange={(e) => setQuickAddDate(e.target.value)}
                style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border, colorScheme: 'dark' }}
                data-testid="quick-add-date"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: theme.textMuted }}>Categoría</label>
              <Input
                type="text"
                list="quick-add-categories"
                value={quickAddCategory}
                onChange={(e) => setQuickAddCategory(e.target.value)}
                placeholder="ej. Comida, Transporte..."
                style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border }}
                data-testid="quick-add-category"
              />
              <datalist id="quick-add-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: theme.textMuted }}>Monto ({sym})</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={quickAddAmount}
                onChange={(e) => setQuickAddAmount(e.target.value)}
                placeholder="0.00"
                style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border }}
                data-testid="quick-add-amount"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setQuickAddOpen(false)}
                style={{ borderColor: theme.border, color: theme.textSecondary }}>
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  const amt = parseFloat(quickAddAmount);
                  if (!quickAddCategory.trim()) { toast.error('Falta la categoría'); return; }
                  if (!amt || amt <= 0) { toast.error('El monto debe ser mayor a 0'); return; }
                  setQuickAddSaving(true);
                  const ok = onAddFull
                    ? await onAddFull(quickAddDate, quickAddCategory, amt)
                    : false;
                  setQuickAddSaving(false);
                  if (ok) {
                    setQuickAddOpen(false);
                    setQuickAddCategory('');
                    setQuickAddAmount('');
                    setDateRef(quickAddDate);  // jump the day view to the new entry
                    setView('day');
                  }
                }}
                disabled={quickAddSaving || !onAddFull}
                style={{ background: theme.accent, color: theme.bg }}
                data-testid="quick-add-save"
              >
                {quickAddSaving ? 'Guardando...' : 'Guardar gasto'}
              </Button>
            </div>
            <p className="text-[10px] mt-1" style={{ color: theme.textMuted }}>
              Tip: puedes agregar gastos retroactivos. El saldo total se actualiza automáticamente.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const TrackerRow = ({ row, categories, theme, sym, fmt, onUpdate, onDelete }) => {
  const [local, setLocal] = useState({
    label: row.label || '',
    description: row.description || '',
    actual: row.actual || 0,
    date: row.date || new Date().toISOString().split('T')[0],
  });
  useEffect(() => {
    setLocal({
      label: row.label || '', description: row.description || '',
      actual: row.actual || 0, date: row.date || new Date().toISOString().split('T')[0],
    });
  }, [row.id, row.label, row.description, row.actual, row.date]);
  const [creatingNew, setCreatingNew] = useState(false);
  const opts = Array.from(new Set([...(categories || []), ...(local.label ? [local.label] : [])]));
  const commit = (patch) => {
    onUpdate && onUpdate({ ...row, ...local, ...patch });
  };
  return (
    <tr style={{ borderTop: `1px solid ${theme.border}` }} className="group">
      <td className="p-2 text-sm" style={{ width: '180px' }}>
        {creatingNew || opts.length === 0 ? (
          <input autoFocus value={local.label}
            onChange={(e) => setLocal({ ...local, label: e.target.value })}
            onBlur={() => { setCreatingNew(false); if (local.label !== row.label) commit({ label: local.label }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            placeholder={opts.length === 0 ? "Crea antes un presupuesto..." : "Categoría"}
            className="bg-transparent border-0 outline-none text-sm w-full px-1.5 py-1 rounded hover:bg-white/5"
            style={{ color: theme.textPrimary }} />
        ) : (
          <select value={local.label || ''}
            onChange={(e) => {
              const v = e.target.value;
              setLocal({ ...local, label: v }); commit({ label: v });
            }}
            className="bg-transparent border-0 outline-none text-sm w-full px-1.5 py-1 rounded cursor-pointer hover:bg-white/5"
            style={{ color: theme.textPrimary, colorScheme: 'dark' }}
            title="Solo puedes elegir categorías que ya tengan presupuesto. Crea el presupuesto en 'Presupuesto por categoría'.">
            {!local.label && <option value="" disabled>Elige una categoría</option>}
            {opts.map(c => <option key={c} value={c} style={{ background: theme.card, color: theme.textPrimary }}>{c}</option>)}
          </select>
        )}
      </td>
      <td className="p-2 text-sm">
        <input value={local.description}
          onChange={(e) => setLocal({ ...local, description: e.target.value })}
          onBlur={() => { if (local.description !== row.description) commit({ description: local.description }); }}
          placeholder="Descripción"
          className="bg-transparent border-0 outline-none text-sm w-full px-1.5 py-1 rounded hover:bg-white/5"
          style={{ color: theme.textMuted }} />
      </td>
      <td className="p-2 text-right text-sm tabular-nums" style={{ width: '110px' }}>
        <input type="number" inputMode="decimal" value={local.actual === 0 || local.actual === '0' ? '' : local.actual}
          onChange={(e) => setLocal({ ...local, actual: e.target.value })}
          onBlur={() => { const n = parseFloat(local.actual) || 0; if (n !== parseFloat(row.actual)) commit({ actual: n }); }}
          placeholder="0"
          className="bg-transparent border-0 outline-none text-sm w-full text-right tabular-nums px-1.5 py-1 rounded hover:bg-white/5 font-semibold"
          style={{ color: theme.error }} />
      </td>
      <td className="p-1 text-center" style={{ width: '32px' }}>
        <button onClick={() => onDelete && onDelete(row.id)}
          className="opacity-0 group-hover:opacity-100 p-1" style={{ color: theme.textMuted }}>
          <Trash2 size={12} />
        </button>
      </td>
    </tr>
  );
};

const PLAN_DISPLAY_NAME = {
  free: 'Free', light: 'Light', pro: 'Pro', vip: 'Premium', trimestral: 'Trimestral',
};

const BudgetUpgradeGate = ({ theme, plan }) => (
  <div data-testid="budget-locked" className="min-h-[70vh] flex items-center justify-center px-4">
    <div className="max-w-xl w-full rounded-3xl p-10 text-center relative overflow-hidden"
      style={{
        background: `linear-gradient(160deg, ${theme.primary}25, ${theme.card} 60%)`,
        border: `1px solid ${theme.accent}40`,
        boxShadow: `0 24px 60px ${theme.primary}30`,
      }}>
      {/* Decorative accents */}
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${theme.accent}40, transparent 70%)` }} />
      <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${theme.primary}30, transparent 70%)` }} />

      <div className="relative">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
          style={{ background: `${theme.accent}25`, border: `1px solid ${theme.accent}60` }}>
          <Lock size={28} style={{ color: theme.accent }} />
        </div>

        <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: theme.textMuted }}>
          Función exclusiva
        </p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-3" style={{ color: theme.accent }}>
          Presupuesto Premium
        </h1>
        <p className="text-sm mb-6 leading-relaxed" style={{ color: theme.textSecondary }}>
          El módulo de presupuestos —ingresos, gastos del día, ahorros, deudas, gráficos y proyecciones—
          está disponible <strong style={{ color: theme.textPrimary }}>únicamente en el plan Premium</strong>.
          Tu plan actual es <strong style={{ color: theme.textPrimary }}>{PLAN_DISPLAY_NAME[plan] || plan}</strong>.
        </p>

        <ul className="text-left text-sm space-y-2 mb-7 max-w-md mx-auto" style={{ color: theme.textSecondary }}>
          <li className="flex items-start gap-2"><Crown size={14} className="mt-0.5 shrink-0" style={{ color: theme.accent }} /> Tablas de ingresos, gastos fijos y variables</li>
          <li className="flex items-start gap-2"><Crown size={14} className="mt-0.5 shrink-0" style={{ color: theme.accent }} /> Tracker diario con FAB de gasto rápido</li>
          <li className="flex items-start gap-2"><Crown size={14} className="mt-0.5 shrink-0" style={{ color: theme.accent }} /> Savings goals con aportes mensuales</li>
          <li className="flex items-start gap-2"><Crown size={14} className="mt-0.5 shrink-0" style={{ color: theme.accent }} /> Proyecciones, balance arrastrado y gráficos</li>
        </ul>

        <Link to="/membership">
          <Button data-testid="upgrade-to-premium-button"
            className="px-8 py-6 text-base font-medium"
            style={{ background: theme.accent, color: theme.bg, boxShadow: `0 8px 24px ${theme.accent}50` }}>
            <Crown size={16} className="mr-2" /> Pasar a Premium
          </Button>
        </Link>
        <p className="text-[11px] mt-4" style={{ color: theme.textMuted }}>
          Desbloqueá Presupuesto + todo lo demás ilimitado por $11/mes.
        </p>
      </div>
    </div>
  </div>
);

const Budget = () => {
  const [rows, setRows] = useState([]);
  const [bills, setBills] = useState([]);
  const [savings, setSavings] = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [month, setMonth] = useState(new Date().getMonth());
  const [year] = useState(new Date().getFullYear());
  const [showAddSaving, setShowAddSaving] = useState(false);
  const [savName, setSavName] = useState(''); const [savTarget, setSavTarget] = useState('');
  const [savCurrent, setSavCurrent] = useState(''); const [savStart, setSavStart] = useState('');
  const [savDate, setSavDate] = useState(''); const [savMonthly, setSavMonthly] = useState('');
  const [savStartDate, setSavStartDate] = useState('');
  const { theme } = useTheme();
  const { user } = useAuth();
  const hasBudgetAccess = !!user?.plan_limits?.budget_access;

  // Data fetch — always run while the premium gate is disabled (see below).
  // When the gate is re-enabled, this should be guarded by `hasBudgetAccess` again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      const [b, bl, sv] = await Promise.all([api.get('/budget'), api.get('/bills'), api.get('/savings')]);
      setRows(b.data); setBills(bl.data); setSavings(sv.data);
    } catch (err) { console.debug(err); }
  };

  const sym = CURRENCIES.find(c => c.code === currency)?.symbol || '$';

  // ===== Lifetime Saldo (NEVER resets) — sum across ALL months/years =====
  // Income everywhere minus expenses/trackers/paid-bills/paid-debts everywhere.
  const lifetimeIncome = rows
    .filter((r) => r.type === 'income')
    .reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
  const lifetimeExpenses = rows
    .filter((r) => r.type === 'expense' || r.type === 'tracker')
    .reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
  const lifetimeBills = bills
    .filter((b) => b.paid)
    .reduce((s, b) => s + (parseFloat(b.budget) || parseFloat(b.budget_amount) || 0), 0);
  const lifetimeDebt = rows
    .filter((r) => r.type === 'debt' && r.paid)
    .reduce((s, r) => s + (parseFloat(r.budget) || 0), 0);
  const lifetimeSaldo = lifetimeIncome - lifetimeExpenses - lifetimeBills - lifetimeDebt;

  const monthRows = rows.filter(r => {
    if (!r.date) return false;
    const d = new Date(r.date);
    return d.getMonth() === month && d.getFullYear() === year;
  });

  const rowsByType = (t) => monthRows.filter(r => r.type === t);

  // Aggregated Gastos: combine expense + tracker rows by category label, summed.
  // Also include category-budget keys so the user sees their budgeted categories even with 0 spending.
  const expenseAggregated = (() => {
    const map = {};
    [...rowsByType('expense'), ...rowsByType('tracker')].forEach(r => {
      const k = r.label?.trim() || 'Sin categoría';
      if (!map[k]) map[k] = { id: `agg-${k}`, label: k, actual: 0, _backed: [] };
      map[k].actual += parseFloat(r.actual) || 0;
      map[k]._backed.push(r);
    });
    // Pull category budgets from current month's localStorage
    try {
      const cb = JSON.parse(localStorage.getItem(`teko-cat-budgets-${year}-${month}`) || '{}');
      Object.keys(cb).forEach(k => {
        if (!map[k]) map[k] = { id: `agg-${k}`, label: k, actual: 0, _backed: [], budget: cb[k] };
        else map[k].budget = cb[k];
      });
    } catch (err) { console.debug(err); }
    return Object.values(map);
  })();
  // Add a manual standalone expense aggregate row (becomes type='expense')
  const addExpenseAggregate = async () => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-15`;
    try {
      const { data } = await api.post('/budget', {
        category: 'expense', amount: 0, type: 'expense',
        date: dateStr, currency, period: 'monthly',
        label: '', budget: 0, actual: 0,
      });
      setRows(prev => [...prev, { ...data, type: 'expense', label: '', actual: 0 }]);
    } catch { toast.error('Error'); }
  };
  // Update aggregate. Three cases:
  //  - 0 backed rows (label only came from category-budget): create a new expense row with the amount.
  //  - 1 backed row: update that row directly.
  //  - >1 backed rows: rename label across all (cannot redistribute amount).
  const updateExpenseAggregate = async (u) => {
    const original = expenseAggregated.find(a => a.id === u.id);
    if (!original) return;
    const backed = original._backed;
    const newAmount = parseFloat(u.actual) || 0;
    const newLabel = u.label || '';
    if (backed.length === 0) {
      // No row backs this aggregate (came from category-budget only). Create one if amount > 0.
      if (newAmount > 0 || newLabel !== original.label) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-15`;
        try {
          const { data } = await api.post('/budget', {
            category: 'expense', amount: newAmount, type: 'expense',
            date: dateStr, currency, period: 'monthly',
            label: newLabel || original.label, budget: 0, actual: newAmount,
          });
          await api.put(`/budget/${data.id}`, { type: 'expense' });
          setRows(prev => [...prev, { ...data, type: 'expense', label: newLabel || original.label, actual: newAmount }]);
        } catch { toast.error('Error'); }
      }
      return;
    }
    if (backed.length === 1) {
      const r = backed[0];
      try {
        await api.put(`/budget/${r.id}`, {
          label: newLabel, actual: newAmount, type: r.type,
          amount: newAmount, date: r.date,
        });
        setRows(prev => prev.map(x => x.id === r.id ? { ...x, label: newLabel, actual: newAmount } : x));
      } catch (err) { console.debug(err); }
    } else {
      // Multi-source: rename label across all backed rows; ignore amount changes (sum is read-only)
      try {
        await Promise.all(backed.map(r => api.put(`/budget/${r.id}`, { label: newLabel, type: r.type })));
        setRows(prev => prev.map(x => backed.find(b => b.id === x.id) ? { ...x, label: newLabel } : x));
      } catch (err) { console.debug(err); }
    }
  };
  // Delete: remove all backed entries
  const deleteExpenseAggregate = async (id) => {
    const original = expenseAggregated.find(a => a.id === id);
    if (!original) return;
    try {
      await Promise.all(original._backed.map(r => api.delete(`/budget/${r.id}`)));
      setRows(prev => prev.filter(x => !original._backed.find(b => b.id === x.id)));
    } catch (err) { console.debug(err); }
  };

  const addRow = async (type) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-15`;
    try {
      const { data } = await api.post('/budget', {
        category: type, amount: 0, type: type === 'income' ? 'income' : 'expense',
        date: dateStr, currency, period: 'monthly', label: '', budget: 0, actual: 0, expected: 0,
      });
      await api.put(`/budget/${data.id}`, { type });
      setRows(prev => [...prev, { ...data, type, label: '', budget: 0, actual: 0, expected: 0 }]);
    } catch { toast.error('Error'); }
  };

  const updateRow = async (u) => {
    setRows(prev => prev.map(r => r.id === u.id ? u : r));
    // For debts: when "Pagado" checked, treat actual = budget (otherwise 0).
    const isDebt = u.type === 'debt';
    const debtActual = u.paid ? (parseFloat(u.budget) || 0) : 0;
    const finalActual = isDebt ? debtActual : (parseFloat(u.actual) || 0);
    try {
      await api.put(`/budget/${u.id}`, {
        label: u.label || '', budget: parseFloat(u.budget) || 0,
        actual: finalActual, date: u.date, type: u.type,
        amount: finalActual,
        expected: parseFloat(u.expected) || 0,
        description: u.description || '',
        paid: !!u.paid,
      });
      if (isDebt) {
        // Reflect new actual in local rows
        setRows(prev => prev.map(r => r.id === u.id ? { ...u, actual: debtActual } : r));
      }
    } catch (err) { console.debug(err); }
  };

  const deleteRow = async (id) => {
    try { await api.delete(`/budget/${id}`); setRows(prev => prev.filter(r => r.id !== id)); } catch (err) { console.debug(err); }
  };

  // Tracker categories are STRICTLY the ones that already have a budget set in the
  // "Presupuesto por categoría" section for the current month. This enforces the linkage
  // the product requires: to add an expense, the category must first exist as a budget row.
  const knownCategories = Array.from(new Set([
    ...(() => { try { return Object.keys(JSON.parse(localStorage.getItem(`teko-cat-budgets-${year}-${month}`) || '{}')); } catch { return []; } })(),
    // Backward-compat: include any labels currently present in existing tracker/expense rows so old data doesn't disappear.
    ...rows.filter(r => r.type === 'expense' || r.type === 'tracker').map(r => r.label).filter(Boolean),
  ]));
  const addTrackerRow = async (date) => {
    const dateStr = date || new Date().toISOString().split('T')[0];
    try {
      const { data } = await api.post('/budget', {
        category: 'tracker', amount: 0, type: 'tracker',
        date: dateStr, currency, period: 'daily', label: '', budget: 0, actual: 0,
      });
      await api.put(`/budget/${data.id}`, { type: 'tracker' });
      setRows(prev => [...prev, { ...data, type: 'tracker', label: '', budget: 0, actual: 0 }]);
    } catch { toast.error('Error'); }
  };
  // Quick-add: like addTrackerRow but with category + amount pre-filled in a single round-trip.
  const addTrackerRowFull = async (date, label, amount) => {
    const dateStr = date || new Date().toISOString().split('T')[0];
    const amt = parseFloat(amount) || 0;
    const cleanLabel = (label || '').trim() || 'Sin categoría';
    try {
      const { data } = await api.post('/budget', {
        category: 'tracker', amount: amt, type: 'tracker',
        date: dateStr, currency, period: 'daily', label: cleanLabel,
        budget: 0, actual: amt,
      });
      await api.put(`/budget/${data.id}`, { type: 'tracker', label: cleanLabel, actual: amt });
      setRows(prev => [...prev, { ...data, type: 'tracker', label: cleanLabel, actual: amt, budget: 0 }]);
      toast.success(`Gasto agregado · ${sym} ${fmt(amt)} en ${cleanLabel}`);
      return true;
    } catch { toast.error('No pude agregar el gasto'); return false; }
  };
  const updateTrackerRow = async (rowOrId, patch) => {
    // Support two call signatures:
    //   updateTrackerRow(row, patch) — used by Dashboard FAB
    //   updateTrackerRow(updatedRow) — used by Section/Row inline edits
    let id, finalPatch;
    if (rowOrId && typeof rowOrId === 'object' && patch) {
      id = rowOrId.id; finalPatch = { ...rowOrId, ...patch };
    } else if (rowOrId && typeof rowOrId === 'object') {
      id = rowOrId.id; finalPatch = rowOrId;
    } else { return; }
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...finalPatch } : r));
    try {
      await api.put(`/budget/${id}`, {
        label: finalPatch.label || '',
        actual: parseFloat(finalPatch.actual) || 0,
        amount: parseFloat(finalPatch.actual) || 0,
        date: finalPatch.date,
        type: 'tracker',
        description: finalPatch.description || '',
      });
    } catch (err) { console.debug(err); }
  };
  const deleteTrackerRow = async (id) => {
    try { await api.delete(`/budget/${id}`); setRows(prev => prev.filter(r => r.id !== id)); } catch (err) { console.debug(err); }
  };

  const addBill = async () => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-15`;
    try {
      const { data } = await api.post('/bills', { name: '', due_date: dateStr, budget_amount: 0, actual_amount: 0 });
      setBills(prev => [...prev, { ...data, label: '', budget: 0, actual: 0, date: dateStr }]);
    } catch (err) { console.debug(err); }
  };
  const updateBill = async (u) => {
    setBills(prev => prev.map(b => b.id === u.id ? u : b));
    try {
      await api.put(`/bills/${u.id}`, {
        name: u.label || u.name || '', due_date: u.date || u.due_date,
        budget_amount: parseFloat(u.budget) || 0, 
        actual_amount: u.paid ? (parseFloat(u.budget) || 0) : 0,
        paid: !!u.paid,
      });
    } catch (err) { console.debug(err); }
  };
  const deleteBill = async (id) => { try { await api.delete(`/bills/${id}`); setBills(prev => prev.filter(b => b.id !== id)); } catch (err) { console.debug(err); } };

  // Adapted savings rows for Budget tab Section component (shares /api/savings data with Savings tab).
  // 'paid' is derived from the monthly contribution log for the current (viewed) month.
  // Toggling 'paid' from the planilla updates the same localStorage log used by the Savings tab.
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const getSavingsLog = (goalId) => {
    try { return JSON.parse(localStorage.getItem(`teko-saving-log-${goalId}`) || '{}'); } catch { return {}; }
  };
  const savingsAdapted = savings.map(s => {
    const log = getSavingsLog(s.id);
    const paidThisMonth = (parseFloat(log[monthKey]) || 0) > 0;
    return {
      ...s, label: s.name || '', budget: s.target_amount || 0, monthly: s.monthly_contribution || 0,
      actual: s.current_amount || 0, date: s.goal_date || '', paid: paidThisMonth,
    };
  });
  const addSavingsRow = async () => {
    try {
      const { data } = await api.post('/savings', { name: '', target_amount: 0, current_amount: 0, start_amount: 0, monthly_contribution: 0 });
      setSavings(prev => [...prev, data]);
    } catch { toast.error('Error'); }
  };
  const updateSavingsRow = async (u) => {
    // Intercept 'paid' toggle → mirror to monthly contribution log (same source used by Savings tab)
    const currentAdapted = savingsAdapted.find(x => x.id === u.id);
    const paidChanged = currentAdapted && !!u.paid !== !!currentAdapted.paid;
    if (paidChanged) {
      const log = getSavingsLog(u.id);
      const monthlyAmt = parseFloat(u.monthly) || 0;
      const currentAmt = parseFloat(currentAdapted.actual) || 0;
      let newCurrent = currentAmt;
      if (u.paid) {
        log[monthKey] = monthlyAmt;
        newCurrent = currentAmt + monthlyAmt;
      } else {
        const prevContrib = parseFloat(log[monthKey]) || 0;
        delete log[monthKey];
        newCurrent = Math.max(0, currentAmt - prevContrib);
      }
      localStorage.setItem(`teko-saving-log-${u.id}`, JSON.stringify(log));
      setSavings(prev => prev.map(s => s.id === u.id ? {
        ...s, name: u.label, target_amount: parseFloat(u.budget) || 0,
        monthly_contribution: monthlyAmt, current_amount: newCurrent,
        goal_date: u.date || s.goal_date,
      } : s));
      try {
        await api.put(`/savings/${u.id}`, {
          name: u.label || '', target_amount: parseFloat(u.budget) || 0,
          monthly_contribution: monthlyAmt,
          current_amount: newCurrent, goal_date: u.date || null,
        });
      } catch (err) { console.debug(err); }
      return;
    }
    setSavings(prev => prev.map(s => s.id === u.id ? { ...s, name: u.label, target_amount: parseFloat(u.budget) || 0, monthly_contribution: parseFloat(u.monthly) || 0, current_amount: parseFloat(u.actual) || 0, goal_date: u.date } : s));
    try {
      await api.put(`/savings/${u.id}`, {
        name: u.label || '', target_amount: parseFloat(u.budget) || 0,
        monthly_contribution: parseFloat(u.monthly) || 0,
        current_amount: parseFloat(u.actual) || 0, goal_date: u.date || null,
      });
    } catch (err) { console.debug(err); }
  };
  const deleteSavingsRow = async (id) => {
    try { await api.delete(`/savings/${id}`); setSavings(prev => prev.filter(s => s.id !== id)); } catch (err) { console.debug(err); }
  };

  // Compute totals for header
  const totalIncome = rowsByType('income').reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
  const totalExpense = rowsByType('expense').reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
  const monthBills = bills.filter(b => {
    const dt = b.date || b.due_date;
    if (!dt) return true; // include if no date set
    const d = new Date(dt);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const totalBillsBudget = monthBills.reduce((s, b) => s + (parseFloat(b.budget) || parseFloat(b.budget_amount) || 0), 0);
  const totalBillsActual = monthBills.reduce((s, b) => s + (b.paid ? (parseFloat(b.budget) || parseFloat(b.budget_amount) || 0) : 0), 0);
  const totalSavingsBudget = savings.reduce((s, r) => s + (parseFloat(r.monthly_contribution) || 0), 0);
  const totalSavingsActual = savings.reduce((s, r) => s + (parseFloat(r.current_amount) || 0), 0);
  const totalDebtBudget = rowsByType('debt').reduce((s, r) => s + (parseFloat(r.budget) || 0), 0);
  // For debts: "Pagado" checkbox — when checked, the debt is considered fully paid (actual = budget).
  const totalDebtActual = rowsByType('debt').reduce((s, r) => s + (r.paid ? (parseFloat(r.budget) || 0) : (parseFloat(r.actual) || 0)), 0);

  // Income by category — group same-labeled rows so chart sums them in same bar
  const incomeByCategory = (() => {
    const map = {};
    rowsByType('income').forEach(r => {
      const k = (r.label && r.label.trim()) || 'Sin categoría';
      const v = parseFloat(r.actual) || 0;
      if (v <= 0) return;
      if (!map[k]) map[k] = 0;
      map[k] += v;
    });
    return Object.entries(map).map(([name, monto]) => ({ name, monto })).sort((a, b) => b.monto - a.monto);
  })();
  // Investment: budget = monto inicial, expected = monto esperado, actual = monto actual
  const totalInvBudget = rowsByType('investment').reduce((s, r) => s + (parseFloat(r.budget) || 0), 0);
  const totalInvActual = rowsByType('investment').reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
  const totalInvExpected = rowsByType('investment').reduce((s, r) => s + (parseFloat(r.expected) || 0), 0);

  // Savings contributions for this current Budget month — reuses monthKey / getSavingsLog declared above
  const savingsContribThisMonth = savings.reduce((s, g) => {
    const log = getSavingsLog(g.id);
    return s + (parseFloat(log[monthKey]) || 0);
  }, 0);
  const markSavingMonthContribution = (goalId, amount) => {
    const log = getSavingsLog(goalId);
    log[monthKey] = parseFloat(amount) || 0;
    localStorage.setItem(`teko-saving-log-${goalId}`, JSON.stringify(log));
    // Force re-render
    setSavings((prev) => [...prev]);
  };
  const isContributedThisMonth = (goalId) => {
    const log = getSavingsLog(goalId);
    return (parseFloat(log[monthKey]) || 0) > 0;
  };
  const totalIncomeBudget = totalIncome;
  const totalExpenseBudget = rowsByType('expense').reduce((s, r) => s + (parseFloat(r.budget) || 0), 0);
  // Tracker entries also feed into expenses
  const totalTrackerActual = rowsByType('tracker').reduce((s, r) => s + (parseFloat(r.actual) || 0), 0);
  const totalRemainingBudget = totalIncomeBudget - totalExpenseBudget - totalBillsBudget - totalSavingsBudget - totalDebtBudget - totalInvBudget;
  const totalRemainingActual = totalIncome - totalExpense - totalBillsActual - totalSavingsActual - totalDebtActual - totalInvActual - totalTrackerActual;

  // Spending pie BY CATEGORY (each expense category = its own slice)
  const expenseCategoryGroups = (() => {
    const map = {};
    [...rowsByType('expense'), ...rowsByType('tracker')].forEach(r => {
      const k = r.label?.trim() || 'Sin categoría';
      if (!map[k]) map[k] = 0;
      map[k] += parseFloat(r.actual) || 0;
    });
    return map;
  })();
  const pieColors = ['#D40000', '#FFFFFF', '#FCA311', '#34D399', '#EC4899', '#6366F1', '#F97316', '#A855F7', '#84CC16', '#06B6D4'];
  const pieData = Object.entries(expenseCategoryGroups)
    .filter(([, v]) => v > 0)
    .map(([name, v], i) => ({ name, v, color: pieColors[i % pieColors.length] }));
  const pieTotal = pieData.reduce((s, d) => s + d.v, 0);

  const barData = [
    { name: 'Gastos fijos', Budget: totalBillsBudget, Actual: totalBillsActual },
    { name: 'Gastos', Budget: totalExpenseBudget, Actual: totalExpense + totalTrackerActual },
    { name: 'Ahorros', Budget: totalSavingsBudget, Actual: totalSavingsActual },
    { name: 'Inversión', Budget: totalInvBudget, Actual: totalInvActual },
    { name: 'Deuda', Budget: totalDebtBudget, Actual: totalDebtActual },
  ];

  // ---- SAVINGS TRACKER ----
  const addSavingsGoal = async () => {
    if (!savName || !savTarget) { toast.error('Nombre y meta requeridos'); return; }
    try {
      await api.post('/savings', {
        name: savName, target_amount: parseFloat(savTarget),
        current_amount: parseFloat(savCurrent || '0'), start_amount: parseFloat(savStart || '0'),
        goal_date: savDate, monthly_contribution: parseFloat(savMonthly || '0'),
        start_date: savStartDate,
      });
      setSavName(''); setSavTarget(''); setSavCurrent(''); setSavStart(''); setSavDate(''); setSavMonthly(''); setSavStartDate('');
      setShowAddSaving(false);
      const { data } = await api.get('/savings'); setSavings(data);
    } catch { toast.error('Error'); }
  };
  const deleteSavingsGoal = async (id) => { try { await api.delete(`/savings/${id}`); setSavings(prev => prev.filter(s => s.id !== id)); } catch (err) { console.debug(err); } };
  const updateSavingsField = async (id, key, val) => {
    const goal = savings.find(s => s.id === id); if (!goal) return;
    const updated = { ...goal, [key]: val };
    setSavings(prev => prev.map(s => s.id === id ? updated : s));
    try { await api.put(`/savings/${id}`, { [key]: val }); } catch (err) { console.debug(err); }
  };

  const totalSavingsGoal = savings.reduce((s, g) => s + (g.target_amount || 0), 0);
  const totalSavingsSaved = savings.reduce((s, g) => s + (g.current_amount || 0), 0);
  const totalSavingsLeft = totalSavingsGoal - totalSavingsSaved;
  const totalSavingsMonthly = savings.reduce((s, g) => s + (g.monthly_contribution || 0), 0);

  const exportBudget = async () => { try { const r = await api.get('/export/budget', { responseType: 'blob' }); const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data])); a.download = 'budget.csv'; a.click(); } catch (err) { console.debug(err); } };

  // Normalize bills to use unified field names
  const billsAdapted = monthBills.map(b => ({
    ...b, label: b.label ?? b.name ?? '', date: b.date ?? b.due_date ?? '',
    budget: b.budget ?? b.budget_amount ?? 0, paid: !!b.paid,
    actual: b.paid ? (b.budget ?? b.budget_amount ?? 0) : 0,
  }));

  // Balance = Income - (Expenses + Bills + Tracker)
  const monthBalance = totalIncome - totalExpense - totalBillsActual - totalTrackerActual;
  // Carryover: persists prior months' running balance so the displayed "Balance del mes"
  // is the cumulative life-balance up to and including the selected month.
  const carryover = React.useMemo(() => {
    let sum = 0;
    rows.forEach(r => {
      if (!r.date) return;
      const m = String(r.date).match(/^(\d{4})-(\d{2})-/);
      if (!m) return;
      const rY = parseInt(m[1], 10); const rM = parseInt(m[2], 10) - 1;
      // Only include rows from months strictly before the currently viewed month
      if (rY < year || (rY === year && rM < month)) {
        if (r.type === 'income') sum += parseFloat(r.actual) || 0;
        else if (r.type === 'expense' || r.type === 'tracker') sum -= parseFloat(r.actual) || 0;
        else if (r.type === 'bill') sum -= parseFloat(r.actual) || 0;
      }
    });
    return sum;
  }, [rows, year, month]);
  const balance = monthBalance + carryover;
  const balancePct = totalIncome > 0 ? Math.round((balance / totalIncome) * 100) : 0;

  const inputStyle = { background: theme.elevated, borderColor: theme.border };

  if (user && !hasBudgetAccess) {
    return <BudgetUpgradeGate theme={theme} plan={user?.plan || 'free'} />;
  }

  return (
    <div data-testid="budget-page" className="space-y-4">
      {/* Title */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight" style={{ color: theme.accent }}>Presupuesto</h1>
        <div className="flex gap-2 flex-wrap">
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="text-white w-[130px]" style={inputStyle}><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: theme.card, borderColor: theme.border }}>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}><span translate="no">{m}</span></SelectItem>)}</SelectContent>
          </Select>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="text-white w-[90px]" style={inputStyle}><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: theme.card, borderColor: theme.border }}>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={exportBudget} variant="outline" style={{ borderColor: theme.border, color: theme.textSecondary }}><Download size={16} /></Button>
        </div>
      </div>

      <Tabs defaultValue="budget" className="w-full">
        <TabsList style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <TabsTrigger data-testid="tab-budget" value="budget" className="data-[state=active]:text-white">Presupuesto Mensual</TabsTrigger>
          <TabsTrigger data-testid="tab-tracker" value="tracker" className="data-[state=active]:text-white">Tracker de Gastos del día</TabsTrigger>
          <TabsTrigger data-testid="tab-savings" value="savings" className="data-[state=active]:text-white">Savings Tracker</TabsTrigger>
        </TabsList>

        {/* ==== BUDGET TAB (image 2 style) ==== */}
        <TabsContent value="budget" className="mt-4 space-y-4">
          {/* Balance hero card */}
          <div className="rounded-xl p-6 flex flex-col md:flex-row items-stretch justify-between gap-5 overflow-hidden"
            style={{ background: theme.gradientSoft || `linear-gradient(135deg, ${theme.primary}30, ${theme.card})`, border: `1px solid ${theme.primary}30` }}>
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-widest mb-2" style={{ color: theme.textMuted }}>Balance del mes</p>
              <p className="text-5xl md:text-6xl font-light tracking-tight tabular-nums" style={{ color: balance >= 0 ? theme.success : theme.error }}>
                {balance >= 0 ? '+' : ''}{sym} {fmt(balance)}
              </p>
              <p className="text-sm mt-2" style={{ color: theme.textSecondary }}>
                Mes: {monthBalance >= 0 ? '+' : ''}{sym} {fmt(monthBalance)}
                {carryover !== 0 && (
                  <span> · Acumulado previo: {carryover >= 0 ? '+' : ''}{sym} {fmt(carryover)}</span>
                )}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 flex-1">
              <div className="rounded-md p-4" style={{ background: `${theme.success}20` }}>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: theme.success }}>Ingresos</p>
                <p className="text-xl font-semibold tabular-nums mt-1" style={{ color: theme.success }}>{sym} {fmt(totalIncome)}</p>
              </div>
              <div className="rounded-md p-4" style={{ background: `${theme.error}20` }}>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: theme.error }}>Gastos</p>
                <p className="text-xl font-semibold tabular-nums mt-1" style={{ color: theme.error }}>{sym} {fmt(totalExpense + totalBillsActual + totalTrackerActual)}</p>
              </div>
              <div className="rounded-md p-4" style={{ background: `${theme.accent}20` }}>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: theme.accent }}>Ahorrado</p>
                <p className="text-xl font-semibold tabular-nums mt-1" style={{ color: theme.accent }}>{sym} {fmt(totalSavingsActual)}</p>
              </div>
              {/* Progress bar removed by user request */}
            </div>
          </div>

          {/* ==== LIFETIME SALDO — accumulated across ALL months, never resets ==== */}
          <div
            className="rounded-xl p-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-6"
            style={{
              background: `linear-gradient(135deg, ${theme.accent}25, ${theme.card})`,
              border: `1px solid ${theme.accent}40`,
            }}
            data-testid="budget-lifetime-saldo"
          >
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.25em] mb-1.5" style={{ color: theme.textMuted }}>Saldo total · histórico</p>
              <p
                className="text-3xl sm:text-4xl font-light tracking-tight tabular-nums"
                style={{ color: lifetimeSaldo >= 0 ? theme.success : theme.error }}
              >
                {lifetimeSaldo >= 0 ? '+' : ''}{sym} {fmt(lifetimeSaldo)}
              </p>
              <p className="text-[11px] mt-1.5" style={{ color: theme.textSecondary }}>
                Ingresos totales <span style={{ color: theme.success, fontWeight: 600 }}>{sym} {fmt(lifetimeIncome)}</span>
                {' · '}gastos totales <span style={{ color: theme.error, fontWeight: 600 }}>{sym} {fmt(lifetimeExpenses + lifetimeBills + lifetimeDebt)}</span>
              </p>
            </div>
            <div className="text-xs sm:text-right" style={{ color: theme.textMuted, maxWidth: 220 }}>
              Acumula <strong style={{ color: theme.textPrimary }}>todos los registros</strong> de tus meses. No se reinicia nunca.
            </div>
          </div>

          {/* ==== CATEGORY BUDGETS (set monthly budget per category, expenses auto-deduct) ==== */}
          <CategoryBudgets theme={theme} sym={sym} fmt={fmt}
            month={month} year={year}
            expenseRows={[...rowsByType('expense'), ...rowsByType('tracker')]} />


          {/* Top Row: Donut + Income breakdown + Bar charts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Donut by category */}
            <div className="p-5 rounded-xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <p className="text-[11px] uppercase tracking-widest text-center mb-3" style={{ color: theme.accent }}>Gastos por categoría</p>
              {pieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={pieData} dataKey="v" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}
                        label={({ percent }) => `${Math.round(percent * 100)}%`}
                        labelLine={false}
                        style={{ fontSize: '10px', fill: theme.textPrimary }}>
                        {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, fontSize: '11px' }}
                        formatter={(v, name) => [`${sym} ${fmt(v)} (${pieTotal > 0 ? Math.round(v / pieTotal * 100) : 0}%)`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-1.5 mt-2">
                    {pieData.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px]">
                        <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                        <span className="truncate flex-1" style={{ color: theme.textPrimary }}>{d.name}</span>
                        <span className="tabular-nums shrink-0" style={{ color: theme.textMuted }}>{pieTotal > 0 ? Math.round(d.v / pieTotal * 100) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-center py-12 text-xs" style={{ color: theme.textMuted }}>Sin gastos cargados</p>}
            </div>
            {/* Income breakdown - vertical bars (grouped by category) */}
            <div className="p-5 rounded-xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <p className="text-[11px] uppercase tracking-widest text-center mb-3" style={{ color: theme.accent }}>Ingresos</p>
              {incomeByCategory.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={incomeByCategory}>
                    <XAxis dataKey="name" stroke={theme.textMuted} style={{ fontSize: '9px' }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis stroke={theme.textMuted} style={{ fontSize: '9px' }} tickFormatter={v => fmt(v)} />
                    <Tooltip
                      contentStyle={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, fontSize: '11px' }}
                      formatter={(v) => `${sym} ${fmt(v)}`} />
                    <Bar dataKey="monto" fill={theme.success} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-center py-12 text-xs" style={{ color: theme.textMuted }}>Sin ingresos cargados</p>}
            </div>
            {/* Projection chart — accumulated daily income vs expenses with future projection */}
            <ProjectionChart theme={theme} sym={sym} fmt={fmt} month={month} year={year}
              incomeRows={rowsByType('income')} expenseRows={[...rowsByType('expense'), ...rowsByType('tracker')]} />
          </div>

          {/* Financial Overview - Professional table with editable budget */}
          <FinancialSummary theme={theme} sym={sym} fmt={fmt} month={month} year={year}
            totalIncome={totalIncome} totalIncomeBudget={totalIncomeBudget}
            totalBillsBudget={totalBillsBudget} totalBillsActual={totalBillsActual}
            totalExpenseBudget={totalExpenseBudget} totalExpenseActual={totalExpense + totalTrackerActual}
            totalSavingsBudget={totalSavingsBudget} totalSavingsActualMonth={savingsContribThisMonth}
            totalInvBudget={totalInvBudget} totalInvActual={totalInvActual}
            totalDebtBudget={totalDebtBudget} totalDebtActual={totalDebtActual}
            balance={balance} balancePct={balancePct}
            totalRemainingBudget={totalRemainingBudget} totalRemainingActual={totalRemainingActual}
            monthName={MONTHS[month]} />


          {/* Sections — 2 columns equal width to match Financial Overview row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Ingresos" color={theme.primary}
              rows={rowsByType('income')} fields={[{ key: 'label', label: 'Categoría', type: 'cat-income', placeholder: 'Sueldo, freelance...' }, { key: 'date', label: 'Fecha', type: 'date' }, { key: 'actual', label: 'Monto', type: 'num' }]}
              knownIncomeCats={incomeByCategory.map(x => x.name)}
              onAdd={() => addRow('income')} onUpdate={updateRow} onDelete={deleteRow} theme={theme} sym={sym} />
            <Section title="Gastos" color={theme.accent}
              rows={expenseAggregated} fields={[{ key: 'label', label: 'Categoría', type: 'label', placeholder: 'Comida, transporte...' }, { key: 'actual', label: 'Monto', type: 'num' }]}
              onAdd={addExpenseAggregate} onUpdate={updateExpenseAggregate} onDelete={deleteExpenseAggregate} theme={theme} sym={sym} />
            <Section title="Ahorros" color={theme.light || theme.accent}
              rows={savingsAdapted} fields={[{ key: 'label', label: 'Meta', type: 'label', placeholder: 'Fondo emergencia, viaje...' }, { key: 'date', label: 'Fecha meta', type: 'date' }, { key: 'monthly', label: 'Mensual', type: 'num' }, { key: 'paid', label: 'Pagado', type: 'check' }]}
              onAdd={addSavingsRow} onUpdate={updateSavingsRow} onDelete={deleteSavingsRow} theme={theme} sym={sym} />
            <Section title="Gastos fijos" color={theme.primary}
              rows={billsAdapted} fields={[{ key: 'label', label: 'Descripción', type: 'label', placeholder: 'Alquiler, internet...' }, { key: 'date', label: 'Fecha pago', type: 'date' }, { key: 'budget', label: 'Presupuesto', type: 'num' }, { key: 'paid', label: 'Pagado', type: 'check' }]}
              onAdd={addBill} onUpdate={updateBill} onDelete={deleteBill} theme={theme} sym={sym} />
            <Section title="Deuda" color={theme.error}
              rows={rowsByType('debt')} fields={[{ key: 'label', label: 'Descripción', type: 'label', placeholder: 'Préstamo personal...' }, { key: 'budget', label: 'Presupuesto', type: 'num' }, { key: 'paid', label: 'Pagado', type: 'check' }]}
              onAdd={() => addRow('debt')} onUpdate={updateRow} onDelete={deleteRow} theme={theme} sym={sym} />
            <Section title="Inversión" color={theme.success}
              rows={rowsByType('investment')} fields={[{ key: 'label', label: 'Nombre', type: 'label', placeholder: 'Acciones, ETF, cripto...' }, { key: 'budget', label: 'Inicial', type: 'num' }, { key: 'actual', label: 'Actual', type: 'num' }, { key: 'expected', label: 'Esperado', type: 'num' }, { key: 'date', label: 'Hasta', type: 'date' }]}
              onAdd={() => addRow('investment')} onUpdate={updateRow} onDelete={deleteRow} theme={theme} sym={sym} />
          </div>
        </TabsContent>

        {/* ==== TRACKER DE GASTOS DEL DÍA TAB ==== */}
        <TabsContent value="tracker" className="mt-4 space-y-4">
          <TrackerTab rows={rowsByType('tracker')} theme={theme} sym={sym} fmt={fmt}
            dailyBudget={parseFloat(localStorage.getItem('teko-daily-budget') || '0')}
            categories={knownCategories}
            onUpdate={updateTrackerRow} onDelete={deleteTrackerRow} onAdd={addTrackerRow}
            onAddFull={addTrackerRowFull} />
        </TabsContent>

        {/* ==== SAVINGS TRACKER TAB (image 3 style) ==== */}
        <TabsContent value="savings" className="mt-4 space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="col-span-2 md:col-span-1 rounded-md p-4 text-center" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <p className="text-xl font-serif italic" style={{ color: theme.accent }}>Savings</p>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: theme.textMuted }}>— Dashboard —</p>
              <Button data-testid="add-saving-btn" onClick={() => setShowAddSaving(true)} className="mt-2 text-white" style={{ background: theme.primary }}>
                <Plus size={14} className="mr-1" />Nueva meta
              </Button>
            </div>
            {[
              { label: 'Mensual esperado', v: totalSavingsMonthly, color: theme.accent },
              { label: 'Total ahorrado', v: totalSavingsSaved, color: theme.success },
              { label: 'Meta total', v: totalSavingsGoal },
              { label: 'Falta', v: totalSavingsLeft, color: theme.warning },
            ].map((s, i) => (
              <div key={i} className="rounded-md p-4 text-center" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                <p className="text-[9px] uppercase tracking-widest mb-2" style={{ color: theme.textMuted }}>{s.label}</p>
                <p className="text-xl font-bold tabular-nums" style={{ color: s.color || theme.textPrimary }}>{sym} {fmt(s.v)}</p>
              </div>
            ))}
          </div>

          {/* Add saving dialog */}
          <Dialog open={showAddSaving} onOpenChange={setShowAddSaving}>
            <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <DialogHeader><DialogTitle style={{ color: theme.accent }}>Nueva Meta de Ahorro</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Nombre (Auto, Vacaciones...)" value={savName} onChange={(e) => setSavName(e.target.value)} className="text-white" style={inputStyle} />
                <Input type="number" placeholder="Meta total" value={savTarget} onChange={(e) => setSavTarget(e.target.value)} className="text-white" style={inputStyle} />
                <Input type="number" placeholder="Monto inicial (start)" value={savStart} onChange={(e) => setSavStart(e.target.value)} className="text-white" style={inputStyle} />
                <Input type="number" placeholder="Ahorrado actual" value={savCurrent} onChange={(e) => setSavCurrent(e.target.value)} className="text-white" style={inputStyle} />
                <label className="block text-xs" style={{ color: theme.textMuted }}>Fecha de inicio</label>
                <Input type="date" value={savStartDate} onChange={(e) => setSavStartDate(e.target.value)} className="text-white" style={inputStyle} />
                <label className="block text-xs" style={{ color: theme.textMuted }}>Fecha meta</label>
                <Input type="date" value={savDate} onChange={(e) => setSavDate(e.target.value)} className="text-white" style={inputStyle} />
                <Input type="number" placeholder="Aporte mensual" value={savMonthly} onChange={(e) => setSavMonthly(e.target.value)} className="text-white" style={inputStyle} />
                <Button onClick={addSavingsGoal} className="w-full text-white" style={{ background: theme.primary }}>Crear</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Grid of cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savings.map((g, idx) => {
              const pct = g.target_amount > 0 ? Math.round(((g.current_amount || 0) / g.target_amount) * 100) : 0;
              const left = (g.target_amount || 0) - (g.current_amount || 0);
              const months = g.monthly_contribution > 0 ? Math.max(0, Math.ceil(left / g.monthly_contribution)) : 0;
              const tones = [theme.primary, theme.accent, theme.light || theme.accent, theme.success, theme.warning, '#EC4899', '#6366F1', '#F97316'];
              const color = g.color || tones[idx % tones.length];
              return (
                <div key={g.id} className="rounded-xl overflow-hidden" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                  {/* Header bar with color + rename + color picker */}
                  <div className="flex items-center justify-between px-4 py-2.5" style={{ background: color, color: pickTextColor(color) }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-bold uppercase tracking-widest opacity-80">Savings</p>
                      <input value={g.name || ''} onChange={(e) => updateSavingsField(g.id, 'name', e.target.value)}
                        placeholder="Nombre..."
                        className="text-sm italic bg-transparent border-0 outline-none w-full truncate" style={{ color: pickTextColor(color) }} />
                    </div>
                    <SavingsColorPicker value={color} themeAccent={theme.accent} themeSurface={theme.card} themeBorder={theme.border}
                      onChange={(c) => updateSavingsField(g.id, 'color', c)} />
                  </div>

                  <div className="p-5">
                    {/* Donut with amount in center */}
                    <div className="relative w-40 h-40 mx-auto mb-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={[{ v: pct }, { v: Math.max(0, 100 - pct) }]} dataKey="v" innerRadius={54} outerRadius={72} startAngle={90} endAngle={-270}>
                            <Cell fill={color} />
                            <Cell fill={theme.elevated} />
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-lg font-bold tabular-nums" style={{ color: theme.textPrimary }}>{sym} {fmt(g.current_amount || 0)}</span>
                        <span className="text-xs" style={{ color: theme.textMuted }}>{pct}% de meta</span>
                      </div>
                    </div>

                    {/* Details table */}
                    <div className="space-y-2 text-xs pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
                      <DetailRow label="Fecha inicio" value={g.start_date || ''} onChange={(v) => updateSavingsField(g.id, 'start_date', v)} theme={theme} type="date" />
                      <DetailRow label="Fecha meta" value={g.goal_date || ''} onChange={(v) => updateSavingsField(g.id, 'goal_date', v)} theme={theme} type="date" />
                      <DetailRow label="Meta total" value={g.target_amount} onChange={(v) => updateSavingsField(g.id, 'target_amount', parseFloat(v) || 0)} theme={theme} sym={sym} />
                      <DetailRow label="Inicio" value={g.start_amount || 0} onChange={(v) => updateSavingsField(g.id, 'start_amount', parseFloat(v) || 0)} theme={theme} sym={sym} />
                      <DetailRow label="Ahorrado" value={g.current_amount || 0} onChange={(v) => updateSavingsField(g.id, 'current_amount', parseFloat(v) || 0)} theme={theme} sym={sym} />
                      <div className="flex justify-between py-1"><span style={{ color: theme.textMuted }}>Falta</span><span className="font-semibold tabular-nums" style={{ color: theme.warning }}>{sym} {fmt(left)}</span></div>
                      <DetailRow label="Mensual" value={g.monthly_contribution || 0} onChange={(v) => updateSavingsField(g.id, 'monthly_contribution', parseFloat(v) || 0)} theme={theme} sym={sym} />
                      <div className="flex justify-between py-1"><span style={{ color: theme.textMuted }}>Meses restantes</span><span className="font-semibold" style={{ color: theme.accent }}>{months}</span></div>
                    </div>
                    <button onClick={() => deleteSavingsGoal(g.id)} className="mt-3 text-[11px] w-full py-1 rounded border opacity-60 hover:opacity-100"
                      style={{ color: theme.error, borderColor: theme.border }}>Eliminar</button>
                    {/* Monthly contribution status (current month) */}
                    {(() => {
                      const monthKeyStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                      let log = {};
                      try { log = JSON.parse(localStorage.getItem(`teko-saving-log-${g.id}`) || '{}'); } catch (err) { console.debug(err); }
                      const contributed = parseFloat(log[monthKeyStr] || 0);
                      return (
                        <div className="mt-2 px-2.5 py-2 rounded-md flex items-center justify-between gap-2"
                          style={{ background: contributed > 0 ? `${theme.success}15` : `${theme.warning}15`, border: `1px solid ${contributed > 0 ? theme.success : theme.warning}40` }}>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider" style={{ color: contributed > 0 ? theme.success : theme.warning }}>
                              {contributed > 0 ? 'Aportado este mes' : 'Aún no aportaste este mes'}
                            </p>
                            <p className="text-sm font-semibold tabular-nums mt-0.5" style={{ color: theme.textPrimary }}>
                              {sym} {fmt(contributed)} {contributed > 0 ? '' : `/ ${sym} ${fmt(g.monthly_contribution || 0)}`}
                            </p>
                          </div>
                          <button onClick={() => {
                            const monthKey2 = monthKeyStr;
                            const log2 = (() => { try { return JSON.parse(localStorage.getItem(`teko-saving-log-${g.id}`) || '{}'); } catch { return {}; } })();
                            const monthly = parseFloat(g.monthly_contribution) || 0;
                            if (contributed > 0) {
                              // Toggle off this month's contribution
                              delete log2[monthKey2];
                              localStorage.setItem(`teko-saving-log-${g.id}`, JSON.stringify(log2));
                              updateSavingsField(g.id, 'current_amount', Math.max(0, (parseFloat(g.current_amount) || 0) - contributed));
                            } else {
                              // Mark contribution
                              log2[monthKey2] = monthly;
                              localStorage.setItem(`teko-saving-log-${g.id}`, JSON.stringify(log2));
                              updateSavingsField(g.id, 'current_amount', (parseFloat(g.current_amount) || 0) + monthly);
                            }
                          }}
                            disabled={!g.monthly_contribution}
                            className="text-[10px] px-2 py-1 rounded font-semibold disabled:opacity-40"
                            style={{
                              background: contributed > 0 ? theme.success : theme.warning,
                              color: '#fff',
                            }}>
                            {contributed > 0 ? '✓ Hecho' : 'Marcar'}
                          </button>
                        </div>
                      );
                    })()}
                    {/* Quick add monthly deposit */}
                    <div className="mt-2 flex gap-1.5">
                      <button onClick={() => updateSavingsField(g.id, 'current_amount', (parseFloat(g.current_amount) || 0) + (parseFloat(g.monthly_contribution) || 0))}
                        disabled={!g.monthly_contribution} title="Sumar el aporte mensual al ahorrado"
                        className="flex-1 text-[11px] py-1.5 rounded transition-colors hover:opacity-90 disabled:opacity-40"
                        style={{ background: `${color}20`, color: color, border: `1px solid ${color}50` }}>
                        + {sym} {fmt(g.monthly_contribution || 0)} mensual
                      </button>
                      <QuickAmountButton color={color} theme={theme} sym={sym}
                        onAdd={(amt) => updateSavingsField(g.id, 'current_amount', (parseFloat(g.current_amount) || 0) + amt)} />
                    </div>
                  </div>
                </div>
              );
            })}
            {savings.length === 0 && <div className="col-span-full text-center py-12 rounded-md" style={{ background: theme.card, border: `1px solid ${theme.border}`, color: theme.textMuted }}>Sin metas de ahorro. Agrega la primera.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const DetailRow = ({ label, value, onChange, theme, sym, type = 'num' }) => {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <div className="flex justify-between items-center py-0.5">
      <span style={{ color: theme.textMuted }}>{label}</span>
      <input type={type === 'date' ? 'date' : 'number'} value={local || ''} onChange={(e) => setLocal(e.target.value)} onBlur={() => onChange(local)}
        className="text-right text-xs bg-transparent border-0 outline-none"
        style={{ color: theme.textPrimary, width: type === 'date' ? '115px' : '90px', colorScheme: 'dark' }} />
    </div>
  );
};

// Color picker popover for savings cards. Shows palette of theme-derived + custom colors.
const SAVINGS_PALETTE = [
  // Reds (theme-defaults variations)
  '#D40000', '#E61020', '#FF3344', '#FF6677', '#FFAFB5',
  // Pinks
  '#EC4899', '#F472B6', '#F9A8D4',
  // Oranges/yellow
  '#F97316', '#FBBF24', '#FDE68A',
  // Greens
  '#10B981', '#34D399', '#A7F3D0',
  // Blues
  '#06B6D4', '#3B82F6', '#93C5FD',
  // Purples
  '#A855F7', '#9333EA', '#C4B5FD',
  // Neutrals
  '#FFFFFF', '#A3A3A3', '#525252', '#1F1F1F',
];

const SavingsColorPicker = ({ value, themeSurface, themeBorder, onChange }) => {
  const [custom, setCustom] = useState(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="w-7 h-7 rounded-full ring-2 ring-white/40 transition-transform hover:scale-110"
          style={{ background: value }} title="Cambiar color" />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" style={{ background: themeSurface, border: `1px solid ${themeBorder}` }}>
        <p className="text-[10px] uppercase tracking-widest mb-2 opacity-70">Paleta</p>
        <div className="grid grid-cols-6 gap-1.5 mb-3">
          {SAVINGS_PALETTE.map(c => (
            <button key={c} type="button" onClick={() => onChange(c)}
              className="w-7 h-7 rounded-full hover:scale-110 transition-transform"
              style={{ background: c, border: value === c ? '2px solid white' : '1px solid rgba(255,255,255,0.1)' }} />
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: themeBorder }}>
          <p className="text-[10px] uppercase tracking-widest opacity-70">Personalizado</p>
          <input type="color" value={custom} onChange={(e) => { setCustom(e.target.value); onChange(e.target.value); }}
            className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Inline quick-amount-add for savings cards
const QuickAmountButton = ({ color, theme, sym, onAdd }) => {
  const [amt, setAmt] = useState('');
  const [open, setOpen] = useState(false);
  const submit = () => {
    const v = parseFloat(amt);
    if (!isNaN(v) && v > 0) { onAdd(v); setAmt(''); setOpen(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button title="Sumar otro monto"
          className="w-8 h-8 rounded transition-colors hover:opacity-90 flex items-center justify-center"
          style={{ background: `${color}20`, color, border: `1px solid ${color}50` }}>
          <Plus size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2.5 flex gap-2" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <Input autoFocus type="number" placeholder="Monto" value={amt} onChange={(e) => setAmt(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          className="text-white text-sm h-8 w-32" style={{ background: theme.elevated, borderColor: theme.border }} />
        <Button size="sm" onClick={submit} className="text-white h-8 text-xs" style={{ background: color }}>Sumar</Button>
      </PopoverContent>
    </Popover>
  );
};

// ---- CategoryBudgets: user defines monthly budget per category; expenses auto-deduct ----
const CategoryBudgets = ({ theme, sym, fmt, month, year, expenseRows }) => {
  const storageKey = `teko-cat-budgets-${year}-${month}`;
  const [budgets, setBudgets] = useState({});
  const [newCat, setNewCat] = useState('');
  const [newAmt, setNewAmt] = useState('');

  useEffect(() => {
    try { setBudgets(JSON.parse(localStorage.getItem(storageKey) || '{}')); } catch { setBudgets({}); }
  }, [storageKey]);

  const save = (next) => { setBudgets(next); localStorage.setItem(storageKey, JSON.stringify(next)); };
  const addCat = () => {
    const name = newCat.trim(); const amt = parseFloat(newAmt);
    if (!name || isNaN(amt) || amt <= 0) return;
    save({ ...budgets, [name]: amt });
    setNewCat(''); setNewAmt('');
  };
  const updateCat = (name, amt) => {
    const v = parseFloat(amt) || 0;
    if (v <= 0) { const cp = { ...budgets }; delete cp[name]; save(cp); }
    else save({ ...budgets, [name]: v });
  };
  const removeCat = (name) => { const cp = { ...budgets }; delete cp[name]; save(cp); };

  // Aggregate actual amounts by category from expense rows
  const actualByCat = {};
  expenseRows.forEach(r => {
    const k = r.label?.trim() || 'Sin categoría';
    actualByCat[k] = (actualByCat[k] || 0) + (parseFloat(r.actual) || 0);
  });

  const colors = ['#D40000', '#FFFFFF', '#FCA311', '#34D399', '#EC4899', '#6366F1', '#F97316', '#A855F7', '#84CC16', '#06B6D4'];
  const cats = Object.keys(budgets);

  return (
    <div className="rounded-xl p-5" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-xs uppercase tracking-widest font-semibold" style={{ color: theme.accent }}>Presupuesto por categoría</h3>
          <p className="text-[10px] mt-0.5" style={{ color: theme.textMuted }}>Define un presupuesto y se descontará automáticamente con cada gasto cargado en esa categoría</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Categoría (ej: Comida)" value={newCat} onChange={(e) => setNewCat(e.target.value)}
            className="text-white h-8 text-xs w-44" style={{ background: theme.elevated, borderColor: theme.border }} />
          <Input type="number" placeholder="Presupuesto" value={newAmt} onChange={(e) => setNewAmt(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCat()}
            className="text-white h-8 text-xs w-32" style={{ background: theme.elevated, borderColor: theme.border }} />
          <Button onClick={addCat} size="sm" className="h-8 text-xs text-white" style={{ background: theme.primary }}>
            <Plus size={12} className="mr-1" />Agregar
          </Button>
        </div>
      </div>

      {cats.length === 0 ? (
        <p className="text-center py-6 text-xs" style={{ color: theme.textMuted }}>Aún no defines presupuestos por categoría. Agrega arriba.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
          {cats.map((name, i) => {
            const budget = budgets[name];
            const actual = actualByCat[name] || 0;
            const pct = budget > 0 ? Math.min(100, Math.round((actual / budget) * 100)) : 0;
            const over = budget > 0 && actual > budget;
            const color = colors[i % colors.length];
            return (
              <div key={name} className="py-2.5 group">
                <div className="flex items-center justify-between mb-1.5 gap-2">
                  <span className="text-sm font-medium truncate flex-1" style={{ color: theme.textPrimary }}>{name}</span>
                  <input type="number" value={budget} onChange={(e) => updateCat(name, e.target.value)}
                    className="text-xs text-right tabular-nums bg-transparent border-0 outline-none w-20"
                    style={{ color: theme.textMuted }} />
                  <button onClick={() => removeCat(name)} className="opacity-0 group-hover:opacity-100" style={{ color: theme.error }}>
                    <Trash2 size={11} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: theme.elevated }}>
                    <div className="absolute inset-y-0 left-0 rounded-full transition-all"
                      style={{ width: `${Math.min(100, pct)}%`, background: over ? theme.error : color, boxShadow: over ? `0 0 12px ${theme.error}60` : 'none' }} />
                  </div>
                  <span className="text-[11px] tabular-nums shrink-0" style={{ color: over ? theme.error : theme.textMuted }}>
                    {sym} {fmt(actual)} / {sym} {fmt(budget)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1 gap-2">
                  <p className="text-[10px]" style={{ color: over ? theme.error : theme.success }}>
                    {over
                      ? `Excedido · ${sym} ${fmt(actual - budget)}`
                      : `Disponible · ${sym} ${fmt(Math.max(0, budget - actual))} (${Math.max(0, 100 - pct)}%)`}
                  </p>
                  <p className="text-[10px]" style={{ color: over ? theme.error : theme.textMuted }}>
                    Gastado · {pct}%
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Budget;

// ---- ProjectionChart: end-of-month projection based on accumulated daily income/expenses ----
const ProjectionChart = ({ theme, sym, fmt, month, year, incomeRows, expenseRows }) => {
  // Days in current month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayD = new Date();
  const isCurrentMonth = (todayD.getFullYear() === year && todayD.getMonth() === month);
  const todayDay = isCurrentMonth ? todayD.getDate() : daysInMonth;

  // Build per-day actuals
  const dailyIn = Array(daysInMonth + 1).fill(0); // index 1..daysInMonth
  const dailyOut = Array(daysInMonth + 1).fill(0);
  const parseDay = (dStr) => {
    if (!dStr) return null;
    const m = String(dStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const dY = parseInt(m[1], 10), dM = parseInt(m[2], 10) - 1, dDay = parseInt(m[3], 10);
    if (dY !== year || dM !== month) return null;
    return dDay;
  };
  incomeRows.forEach(r => {
    const d = parseDay(r.date) || 1; // if no date, count toward day 1
    if (d >= 1 && d <= daysInMonth) dailyIn[d] += parseFloat(r.actual) || 0;
  });
  expenseRows.forEach(r => {
    const d = parseDay(r.date) || 1;
    if (d >= 1 && d <= daysInMonth) dailyOut[d] += parseFloat(r.actual) || 0;
  });

  // Accumulated up to today (real)
  let cumIn = 0, cumOut = 0;
  const realData = [];
  for (let d = 1; d <= todayDay; d++) {
    cumIn += dailyIn[d];
    cumOut += dailyOut[d];
    realData.push({ day: d, real_balance: cumIn - cumOut });
  }
  // Average daily for projection (only days with activity OR all days up to today)
  const avgIn = todayDay > 0 ? cumIn / todayDay : 0;
  const avgOut = todayDay > 0 ? cumOut / todayDay : 0;
  // Projection from today+1 to end of month
  const projData = [];
  let pIn = cumIn, pOut = cumOut;
  for (let d = todayDay + 1; d <= daysInMonth; d++) {
    pIn += avgIn;
    pOut += avgOut;
    projData.push({ day: d, proj_balance: pIn - pOut });
  }
  // Combined dataset (a single series with two keys: real_balance for past, proj_balance for future)
  const combined = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const realPoint = realData.find(x => x.day === d);
    const projPoint = projData.find(x => x.day === d);
    combined.push({
      day: d,
      real_balance: realPoint?.real_balance,
      proj_balance: d === todayDay ? realPoint?.real_balance : projPoint?.proj_balance, // bridge so the proj line connects
    });
  }
  const finalBalance = projData.length > 0 ? projData[projData.length - 1].proj_balance : (realData[realData.length - 1]?.real_balance ?? 0);
  const isNegative = finalBalance < 0;

  return (
    <div className="p-5 rounded-xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-widest" style={{ color: theme.accent }}>Proyección de fin de mes</p>
        <p className="text-[10px]" style={{ color: theme.textMuted }}>Día {todayDay} de {daysInMonth}</p>
      </div>
      {realData.length === 0 ? (
        <p className="text-center py-12 text-xs" style={{ color: theme.textMuted }}>Sin datos suficientes para proyectar</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={combined}>
              <XAxis dataKey="day" stroke={theme.textMuted} style={{ fontSize: '9px' }} />
              <YAxis stroke={theme.textMuted} style={{ fontSize: '9px' }} tickFormatter={v => fmt(v)} />
              <Tooltip
                contentStyle={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, fontSize: '11px' }}
                formatter={(v, name) => v == null ? null : [`${sym} ${fmt(v)}`, name === 'real_balance' ? 'Balance real' : 'Proyección']}
                labelFormatter={(d) => `Día ${d}`} />
              <Line type="monotone" dataKey="real_balance" stroke={theme.success} strokeWidth={2.5} dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="proj_balance" stroke={isNegative ? theme.error : theme.accent} strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-3 flex items-center justify-between px-2 py-2 rounded-lg" style={{ background: theme.elevated }}>
            <div>
              <p className="text-[9px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Balance estimado al cierre</p>
              <p className="text-lg font-semibold tabular-nums" style={{ color: isNegative ? theme.error : theme.success }}>
                {finalBalance >= 0 ? '+' : ''}{sym} {fmt(finalBalance)}
              </p>
            </div>
            <div className="flex flex-col gap-1 text-[10px]" style={{ color: theme.textMuted }}>
              <span><span className="inline-block w-3 h-[2px] rounded mr-1.5" style={{ background: theme.success }} />Real</span>
              <span><span className="inline-block w-3 h-[2px] rounded mr-1.5" style={{ background: isNegative ? theme.error : theme.accent, borderTop: `2px dashed ${isNegative ? theme.error : theme.accent}` }} />Proyección</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---- FinancialSummary: editable presupuesto per row, recomputes diferencia ----
const FinancialSummary = ({
  theme, sym, fmt, month, year,
  totalIncome, totalIncomeBudget,
  totalBillsBudget, totalBillsActual,
  totalExpenseBudget, totalExpenseActual,
  totalSavingsBudget, totalSavingsActualMonth,
  totalInvBudget, totalInvActual,
  totalDebtBudget, totalDebtActual,
  balance, balancePct,
  totalRemainingBudget, totalRemainingActual,
  monthName,
}) => {
  const overrideKey = `teko-fs-budget-overrides-${year}-${month}`;
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(overrideKey) || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    try { setOverrides(JSON.parse(localStorage.getItem(overrideKey) || '{}')); } catch { setOverrides({}); }
  }, [overrideKey]);

  const setRowBudget = (key, value) => {
    const next = { ...overrides, [key]: parseFloat(value) || 0 };
    setOverrides(next);
    localStorage.setItem(overrideKey, JSON.stringify(next));
  };
  const getEffectiveBudget = (key, defaultVal) => {
    return Object.prototype.hasOwnProperty.call(overrides, key) ? overrides[key] : defaultVal;
  };

  const rowDefs = [
    { key: 'income', n: 'Ingresos', defB: totalIncomeBudget, a: totalIncome, color: theme.success, isIncome: true },
    { key: 'bills', n: 'Gastos fijos', defB: totalBillsBudget, a: totalBillsActual, color: theme.primary },
    { key: 'expenses', n: 'Gastos', defB: totalExpenseBudget, a: totalExpenseActual, color: theme.error },
    { key: 'savings', n: 'Ahorros (este mes)', defB: totalSavingsBudget, a: totalSavingsActualMonth, color: theme.accent },
    { key: 'investment', n: 'Inversión', defB: totalInvBudget, a: totalInvActual, color: theme.success },
    { key: 'debt', n: 'Deuda', defB: totalDebtBudget, a: totalDebtActual, color: theme.warning },
  ];

  // Compute the overall budget vs actual using overrides where present
  const sumBudgets = rowDefs.reduce((s, r) => s + getEffectiveBudget(r.key, r.defB), 0);
  const incomeBudget = getEffectiveBudget('income', totalIncomeBudget);
  const otherBudget = sumBudgets - incomeBudget;
  const remainingBudget = incomeBudget - otherBudget;

  return (
    <div className="rounded-xl overflow-hidden shadow-2xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
      <div className="px-5 py-4 flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.primaryHover || theme.primary})`, color: '#fff' }}>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">Estado financiero</p>
          <h3 className="text-xl font-light tracking-tight capitalize"><span translate="no">Resumen — {monthName} {year}</span></h3>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">Balance del mes</p>
          <p className={`text-2xl font-light tabular-nums ${balance >= 0 ? '' : 'opacity-95'}`}>
            {balance >= 0 ? '+' : ''}{sym} {fmt(balance)}
          </p>
        </div>
      </div>
      <table className="w-full">
        <thead><tr style={{ background: theme.elevated }}>
          <th className="p-3 text-left text-[10px] uppercase tracking-widest font-semibold" style={{ color: theme.textMuted }}>Categoría</th>
          <th className="p-3 text-right text-[10px] uppercase tracking-widest font-semibold" style={{ color: theme.textMuted }}>Presupuesto</th>
          <th className="p-3 text-right text-[10px] uppercase tracking-widest font-semibold" style={{ color: theme.textMuted }}>Actual</th>
          <th className="p-3 text-right text-[10px] uppercase tracking-widest font-semibold" style={{ color: theme.textMuted }}>Diferencia</th>
        </tr></thead>
        <tbody>
          {rowDefs.map((r) => {
            const b = getEffectiveBudget(r.key, r.defB);
            const diff = r.a - b;
            const isPositive = r.isIncome ? diff >= 0 : diff <= 0;
            return (
              <tr key={r.key} className="hover:bg-white/[0.02] transition-colors" style={{ borderBottom: `1px solid ${theme.border}` }}>
                <td className="p-3 text-sm font-medium flex items-center gap-2.5">
                  <div className="w-1 h-6 rounded-full" style={{ background: r.color }} />
                  <span style={{ color: theme.textPrimary }}>{r.n}</span>
                </td>
                <td className="p-2 text-right text-sm tabular-nums">
                  <EditablePresupuesto value={b} onCommit={(v) => setRowBudget(r.key, v)} theme={theme} sym={sym} />
                </td>
                <td className="p-3 text-right text-sm tabular-nums font-semibold" style={{ color: theme.textPrimary }}>{sym} {fmt(r.a)}</td>
                <td className="p-3 text-right text-sm font-semibold tabular-nums" style={{ color: isPositive ? theme.success : theme.error }}>
                  {diff >= 0 ? '+' : ''}{sym} {fmt(diff)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {(() => {
            const incomeB = getEffectiveBudget('income', totalIncomeBudget);
            const outflowB = ['bills','expenses','savings','investment','debt']
              .reduce((s, k) => s + getEffectiveBudget(k, rowDefs.find(r => r.key === k)?.defB || 0), 0);
            const remainingB = incomeB - outflowB;
            const remainingA = totalRemainingActual;
            const isOk = remainingA >= 0;
            return (
              <tr style={{ background: `${theme.accent}15`, borderTop: `2px solid ${theme.accent}` }}>
                <td className="p-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2.5" style={{ color: theme.accent }}>
                  <div className="w-1 h-7 rounded-full" style={{ background: theme.accent }} />
                  Restante
                </td>
                <td className="p-3 text-right text-sm tabular-nums font-bold" style={{ color: theme.textPrimary }}>{sym} {fmt(remainingB)}</td>
                <td className="p-3 text-right text-sm tabular-nums font-bold" style={{ color: isOk ? theme.success : theme.error }}>
                  {remainingA >= 0 ? '+' : ''}{sym} {fmt(remainingA)}
                </td>
                <td className="p-3 text-right text-sm font-bold tabular-nums" style={{ color: (remainingA - remainingB >= 0) ? theme.success : theme.error }}>
                  {(remainingA - remainingB) >= 0 ? '+' : ''}{sym} {fmt(remainingA - remainingB)}
                </td>
              </tr>
            );
          })()}
        </tfoot>
      </table>
      <p className="px-5 py-2 text-[10px] tracking-wider" style={{ color: theme.textMuted, borderTop: `1px solid ${theme.border}` }}>
        Tip: haz clic sobre cualquier presupuesto para editarlo · La diferencia se recalcula automáticamente.
      </p>
    </div>
  );
};

// Inline editable budget cell
const EditablePresupuesto = ({ value, onCommit, theme, sym }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(String(value ?? 0));
  useEffect(() => { setLocal(String(value ?? 0)); }, [value]);
  if (editing) {
    return (
      <input autoFocus type="number" inputMode="decimal" value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setEditing(false); onCommit(local); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { setEditing(false); onCommit(local); } if (e.key === 'Escape') { setEditing(false); setLocal(String(value ?? 0)); } }}
        className="text-right text-sm bg-transparent border-0 outline-none tabular-nums w-28 px-2 py-1 rounded"
        style={{ color: theme.textPrimary, background: theme.elevated }} />
    );
  }
  return (
    <button onClick={() => setEditing(true)}
      className="text-right tabular-nums px-2 py-1 rounded hover:bg-white/5 transition-colors"
      style={{ color: theme.textMuted }} title="Click para editar">
      {sym} {(parseFloat(value) || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })}
    </button>
  );
};
